// ABOUTME: Shopify product form custom element handling add-to-cart with purchase limit enforcement.
// ABOUTME: Supports per-order (buyLimit) and weekly (weeklyBuyLimit) limits via data attributes and window.customerLimit.
if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
      this.checkCheckoutButton();
    }

    /**
     * Fetches current cart and returns all limit-related variables in one place.
     *
     * @returns {object} ctx
     * @returns {string}  ctx.productId       - Shopify numeric product ID (from hidden input)
     * @returns {string}  ctx.variantId        - Currently selected variant ID
     * @returns {number}  ctx.buyLimit         - Per-order max quantity (data-buy-limit). Defaults to Infinity when unset.
     * @returns {number}  ctx.weeklyBuyLimit   - Weekly max quantity (data-weekly-buy-limit). NaN when unset.
     * @returns {number}  ctx.customerWeeklyPurchased - How many units the customer already bought THIS WEEK (window.customerLimit). 0 for guests.
     * @returns {number}  ctx.cartQuantity     - Quantity of this product already in the cart.
     * @returns {number}  ctx.requestedQuantity - Quantity the customer is trying to add right now.
     * @returns {object}  ctx.cartData         - Full cart JSON response.
     */
    async getLimitContext() {
      const cartResponse = await fetch(`${theme.routes.cart}.js`);
      if (!cartResponse.ok) throw new Error(cartResponse.status);
      const cartData = await cartResponse.json();

      const productId = this.form.querySelector('[name="product-id"]').value;
      const variantId = this.form.querySelector('[name="id"]').value;
      const quantityInput = this.form.querySelector('[name="quantity"]');
      const shopifyGid = `gid://shopify/Product/${productId}`;

      return {
        productId,
        variantId,
        buyLimit:                 parseInt(this.dataset.buyLimit, 10) || Infinity,
        weeklyBuyLimit:           parseInt(this.dataset.weeklyBuyLimit, 10),
        customerWeeklyPurchased:  window.customerLimit?.[shopifyGid]
                                    ? parseInt(window.customerLimit[shopifyGid], 10)
                                    : 0,
        cartQuantity:             cartData.items.find(item => item.product_id == productId)?.quantity || 0,
        requestedQuantity:        quantityInput ? parseInt(quantityInput.value, 10) : 1,
        cartData,
      };
    }

    /**
     * Hides dynamic checkout (Buy Now) button when limits are already reached.
     * Runs on page load, not on submit.
     */
    async checkCheckoutButton() {
      const checkoutButton = this.querySelector('.product-info__dynamic-checkout-button');
      if (!checkoutButton) return;

      const ctx = await this.getLimitContext();

      // Guests cannot use Buy Now on weekly-limited products — login required to track purchases
      const guestBlocked = !window.loggedIn && !isNaN(ctx.weeklyBuyLimit);

      // Weekly limit: hide if customer's weekly purchases + cart + 1 would exceed it
      const weeklyExceeded = !isNaN(ctx.weeklyBuyLimit)
        && (ctx.customerWeeklyPurchased + ctx.cartQuantity + 1 > ctx.weeklyBuyLimit);

      // Per-order limit: hide if cart + 1 would exceed it
      const orderExceeded = isFinite(ctx.buyLimit)
        && (ctx.cartQuantity + 1 > ctx.buyLimit);

      checkoutButton.style.display = (guestBlocked || weeklyExceeded || orderExceeded) ? 'none' : 'block';
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      // Ignore signup forms that may be nested inside the product section
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      // Prevent double-clicks while a submission is in progress
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      // --- Pre-submit limit gate ---
      const limitExceeded = await this.checkPurchaseLimit();
      if (limitExceeded) return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete before redirecting to cart page
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        // Fetch updated cart to dispatch accurate events
        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        const oldCartData = await oldCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Dispatch different events depending on whether the item was already in cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
      this.checkCheckoutButton();
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Pre-submit limit check. Validates both per-order and weekly limits.
     * If a limit is exceeded, adjusts the quantity input and shows an error.
     *
     * @returns {boolean} true if limit exceeded (caller should abort submit), false otherwise.
     */
    async checkPurchaseLimit() {
      const ctx = await this.getLimitContext();
      // Guests cannot add weekly-limited products — require login to track weekly purchases
      if (!isNaN(ctx.weeklyBuyLimit) && !window.loggedIn) {
        this.setErrorMsgState(`Sepete ürün ekleyebilmek için giriş yapınız.`);
        return true;
      }

      // --- Per-order limit: cartQuantity + requestedQuantity vs buyLimit ---
      // customerWeeklyPurchased is NOT included here — this is about the current order only.
      if (isFinite(ctx.buyLimit) && ctx.cartQuantity + ctx.requestedQuantity > ctx.buyLimit) {
        const maxCanAdd = ctx.buyLimit - ctx.cartQuantity;
        return this.applyLimitError(maxCanAdd, ctx.buyLimit, false);
      }

      // --- Weekly limit: customerWeeklyPurchased + cartQuantity + requestedQuantity vs weeklyBuyLimit ---
      // Only checked when weeklyBuyLimit is set (non-NaN).
      if (!isNaN(ctx.weeklyBuyLimit)
        && ctx.customerWeeklyPurchased + ctx.cartQuantity + ctx.requestedQuantity > ctx.weeklyBuyLimit) {
        const maxCanAdd = ctx.weeklyBuyLimit - ctx.customerWeeklyPurchased - ctx.cartQuantity;
        return this.applyLimitError(maxCanAdd, ctx.weeklyBuyLimit, true);
      }

      return false;
    }

    /**
     * Adjusts the quantity input to maxCanAdd and shows an appropriate error message.
     *
     * @param {number}  maxCanAdd - How many more units the customer can add (can be <= 0).
     * @param {number}  limit     - The limit that was exceeded (for the error message).
     * @param {boolean} isWeekly  - Whether this is a weekly limit (changes wording).
     * @returns {boolean} Always returns true (limit was exceeded).
     */
    applyLimitError(maxCanAdd, limit, isWeekly) {
      const quantityInput = this.form.querySelector('[name="quantity"]');
      const periodLabel = isWeekly ? 'haftalık ' : '';

      if (maxCanAdd <= 0) {
        if (quantityInput) quantityInput.value = 1;
        this.setErrorMsgState(`Bu ürün için ${periodLabel}sepete ekleme limitine ulaştınız.`);
      } else {
        if (quantityInput) quantityInput.value = maxCanAdd;
        this.setErrorMsgState(
          `Bu ürün için ${periodLabel}en fazla ${limit} adet satın alabilirsiniz. ${maxCanAdd} adet daha ekleyebilirsiniz.`
        );
      }

      setTimeout(() => this.setErrorMsgState(false), 3000);
      return true;
    }

    /**
     * Shows/hides an error message inside .js-form-error element.
     * @param {string|string[]|false} [error=false] - Error string(s) to display, or false to hide.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
